import { Injectable } from '@angular/core';
import {
  AuthService,
  DocumentTemplatesService,
  ExportDataService, GungFlowService, GungModalService,
  NavbarConfigService,
  Product,
  ProductExportActionConfigService,
  ProductExportService, SelectedCustomerService
} from 'gung-standard';
import { first, forkJoin, from, map, mergeMap, Observable, of, switchMap } from 'rxjs';
import { ExportSelection, SelectionAction } from 'gung-list';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { ExportDragProductService } from 'gung-dynamic-pdf';

enum ExportSelectionDataType {
  Xlsx,
  Csv,
  Img,
  ProductInfo,
  ProductStock,
  ProductStockHedlund
}

@Injectable({
  providedIn: 'root'
})
export class CultProductExportActionConfigService extends ProductExportActionConfigService {

  constructor(
    protected productExportService: ProductExportService,
    protected authService: AuthService,
    protected modalService: NgbModal,
    protected router: Router,
    protected documentTemplatesService: DocumentTemplatesService,
    protected navbarConfigService: NavbarConfigService,
    protected exportDragProductService: ExportDragProductService,
    private exportDataService: ExportDataService,
    private selectedCustomerService: SelectedCustomerService,
    private gungFlowService: GungFlowService,
    private gungModalService: GungModalService
  ) {
    super(productExportService, authService, modalService, router, documentTemplatesService, navbarConfigService, exportDragProductService);
  }

  getSelectionActions(): Observable<SelectionAction<Product>[]> {
    return this.authService.getRoles().pipe(
      first(),
      map(roles => {
        if (roles.includes('SALES') || roles.includes('ADMIN')) {
          return [this.getExportProductStockAction(), this.getExportProductStockActionHedlund(), this.getExportProductInfoAction()];
        }
        return [
          // this.getExportXlsxAction(),
          // this.getExportCsvAction(),
          // this.getExportImgAction(),
          this.getExportProductInfoAction()
        ];
      })
    );
  }

  getExportXlsxAction(): SelectionAction<Product> {
    const action: SelectionAction<Product> = {
      label: 'EXPORT_XLSX',
      performAction: (selection: ExportSelection<Product>) => {
        this.buildExportRequest(ExportSelectionDataType.Xlsx, Object.values(selection.selectedItems))
          .pipe(
            mergeMap((request: { isValid: boolean; body: any }) => {
              if (request.isValid) {
                return this.exportDataService.exportProductsAsExcel(request.body);
              }

              return of();
            })
          )
          .subscribe();

        return of();
      }
    };

    return action;
  }

  getExportCsvAction(): SelectionAction<Product> {
    const action: SelectionAction<Product> = {
      label: 'EXPORT_CSV',
      performAction: (selection: ExportSelection<Product>) => {
        this.buildExportRequest(ExportSelectionDataType.Csv, Object.values(selection.selectedItems))
          .pipe(
            mergeMap((request: { isValid: boolean; body: any }) => {
              if (request.isValid) {
                return this.exportDataService.exportProductsAsCsv(request.body);
              }

              return of();
            })
          )
          .subscribe();

        return of();
      }
    };

    return action;
  }

  getExportImgAction(): SelectionAction<Product> {
    const action: SelectionAction<Product> = {
      label: 'EXPORT_IMG',
      performAction: (selection: ExportSelection<Product>) => {
        this.buildExportRequest(ExportSelectionDataType.Img, Object.values(selection.selectedItems))
          .pipe(
            mergeMap((request: { isValid: boolean; body: any }) => {
              if (request.isValid) {
                return this.exportDataService.exportProductsAsImageZip(request.body);
              }

              return of();
            })
          )
          .subscribe();

        return of();
      }
    };

    return action;
  }

  getExportProductInfoAction(): SelectionAction<Product> {
    const action: SelectionAction<Product> = {
      label: 'EXPORT_PRODUCT_INFO',
      performAction: (selection: ExportSelection<Product>) => {
        this.buildExportRequest(ExportSelectionDataType.ProductInfo, Object.values(selection.selectedItems))
          .pipe(
            mergeMap((request: { isValid: boolean; body: any }) => {
              if (request.isValid) {
                return this.exportDataService.exportProductsAsPdf(request.body);
              }

              return of();
            })
          )
          .subscribe();

        return of();
      }
    };

    return action;
  }

  getExportProductStockAction(): SelectionAction<Product> {
    const action: SelectionAction<Product> = {
      label: 'EXPORT_PRODUCT_STOCK_CULT',
      performAction: (selection: ExportSelection<Product>) => {
        this.buildExportRequest(ExportSelectionDataType.ProductStock, Object.values(selection.selectedItems))
          .pipe(
            mergeMap((request: { isValid: boolean; body: any }) => {
              if (request.isValid) {
                console.log(request.body);
                return this.exportDataService.exportProductsAsPdf(request.body);
              }

              return of();
            })
          )
          .subscribe();

        return of();
      }
    };

    return action;
  }

  getExportProductStockActionHedlund(): SelectionAction<Product> {
    const action: SelectionAction<Product> = {
      label: 'EXPORT_PRODUCT_STOCK_HEDLUND',
      performAction: (selection: ExportSelection<Product>) => {
        this.buildExportRequest(ExportSelectionDataType.ProductStockHedlund, Object.values(selection.selectedItems))
          .pipe(
            mergeMap((request: { isValid: boolean; body: any }) => {
              if (request.isValid) {
                console.log(request.body);
                return this.exportDataService.exportProductsAsPdf(request.body);
              }

              return of();
            })
          )
          .subscribe();

        return of();
      }
    };

    return action;
  }

  private buildExportRequest(
    exportSelectionDataType: ExportSelectionDataType,
    itemList: Product[]
  ): Observable<{ isValid: boolean; body: any }> {
    // build request body
    return this.getExportRequestBodyByType(exportSelectionDataType, itemList).pipe(
      first(),
      switchMap((data: any) => {
        return forkJoin({
          data: of(data),
          modalOutput: from(this.gungModalService.openConfirmExportModal(data.recipient)).pipe(first())
        });
      }),
      switchMap(result => {
        let isValid = false;
        if (result.modalOutput && result.modalOutput.email) {
          result.data.recipient = result.modalOutput.email;
          isValid = true;
        }

        return of({ isValid, body: result.data });
      })
    );
  }

  private getExportRequestBodyByType(
    exportSelectionDataType: ExportSelectionDataType,
    itemList: Product[]
  ): Observable<any> {
    return forkJoin([
      this.authService.getCurrentUser().pipe(first()),
      this.selectedCustomerService.getSelectedCustomer().pipe(first()),
      this.gungFlowService.getSelectedFlow().pipe(first())
    ]).pipe(
      map(([user, customer, flow]) => {
        // Product Info and Stock has two additional parameters
        // template and params
        if (
          [ExportSelectionDataType.ProductInfo, ExportSelectionDataType.ProductStock, ExportSelectionDataType.ProductStockHedlund].includes(exportSelectionDataType)
        ) {
          var templateValue =
            exportSelectionDataType === ExportSelectionDataType.ProductInfo
              ? 'PRODUCT_INFORMATION_EXPORT'
              : 'PRODUCT_STOCK_EXPORT';
          const paramsValue = {
            stockId: user.managedMultistockIds[0],
            customerId: customer.id,
            flowId: flow.id,
            dateCreated: new Date().toDateString(),
            brand: this.selectStockBrand(exportSelectionDataType)
          };

          return {
            ids: itemList.map(x => x.id),
            recipient: user.email,
            customerId: customer.id,
            flowId: flow.id,
            template: templateValue,
            params: paramsValue
          };
        }

        return {
          ids: itemList.map(x => x.id),
          recipient: user.email,
          customerId: customer.id,
          flowId: flow.id
        };
      })
    );
  }

  private selectStockBrand(exportType: ExportSelectionDataType) {
    switch (exportType) {
      case ExportSelectionDataType.ProductStock: {
        return 'CULT';
      }

      case ExportSelectionDataType.ProductStockHedlund: {
        return 'HEDLUND';
      }

      case ExportSelectionDataType.ProductInfo: {
        return '';
      }
      default: {
        return '';
      }

    }
  }
}
